import { Box } from "@chakra-ui/react";
import clsx from "clsx";
import type { Embed } from "powerbi-client";
import {
  forwardRef,
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

import { useAppSelector } from "@/reduxHooks";
import { sidebarOpen } from "@/slices/sidebar-slice";
import { AppDispatch, RootState } from "@/store";

import { useDashboard } from "../hooks";
import { setEmbedPosition } from "../redux/dashboard-slice";

import { ErrorView } from "./ErrorView";
import { LoadingView } from "./LoadingView";
import { NoDashboard } from "./NoDashboard";
import { OutdatedAlert } from "./outdated-alert";
import { PowerBIEmbed } from "./PowerBIEmbed";

const DOCKED_STYLES_BASE = {
  position: "absolute",
  zIndex: 1,
  visibility: "hidden", // Start hidden
  opacity: 0,
  top: "0px",
  right: "0px",
  width: "0px",
  height: "0px",
};

declare global {
  interface Window {
    report: Report;
  }
}

const BaseLayout = forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode;
    dockedStyles: any;
    component: React.ReactNode;
    isSidebarOpen: boolean;
  }
>(({ children, dockedStyles, component, isSidebarOpen }, ref) => {
  return (
    <Box
      className="analysis-layout-container"
      ref={ref}
      pos="relative"
      h="100%"
    >
      <Box
        sx={{
          ...dockedStyles,
          maxWidth: isSidebarOpen
            ? "calc(100dvw - 19.5rem)"
            : "calc(100dvw - 4rem)",
          transition: isSidebarOpen ? "none" : "max-width 250ms ease",
        }}
      >
        {component}
      </Box>
      <Box className="outlet-container" h="100%">
        {children}
      </Box>
    </Box>
  );
});

const AnalysisLayout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const params = useParams<{ analysisId: string; dashboardId?: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const embedPosition = useSelector(
    (state: RootState) => state.dashboard.embedPosition
  );
  const previousPathRef = useRef<string>();
  const layoutContainerRef = useRef<HTMLDivElement>(null); // Ref for the layout container
  const [dockedStyles, setDockedStyles] = useState(DOCKED_STYLES_BASE);

  const isSidebarOpen = useAppSelector(sidebarOpen);

  const {
    embedConfig,
    error,
    isLoading,
    hasDashboard,
    fetchDashboardData,
    isOutdated,
  } = useDashboard(params.analysisId || "");

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Effect to calculate docked position
  const calculateDockedPosition = useCallback(() => {
    const placeholder = document.getElementById("dashboard-embed-placeholder");
    const layoutContainer = layoutContainerRef.current;

    if (placeholder && layoutContainer) {
      const placeholderRect = placeholder.getBoundingClientRect();
      const containerRect = layoutContainer.getBoundingClientRect();

      // Calculate position relative to the layout container
      const top = placeholderRect.top - containerRect.top;

      const height = placeholderRect.height;

      setDockedStyles((prevStyles) => ({
        ...prevStyles,
        top: `${top}px`,
        right: `0px`,
        width: "100%",
        height: `${height}px`,
        visibility: "visible",
        opacity: 1,
      }));
    } else {
      // Reset if placeholder not found (e.g., navigating away)
      setDockedStyles(DOCKED_STYLES_BASE);
    }
  }, []);

  useEffect(() => {
    const isOnDashboard = location.pathname.includes("/dashboard");
    const isDashboardTab = location.search.includes("tab=0");

    if (embedPosition === "docked" && isOnDashboard && isDashboardTab) {
      const initialQuickDelay = 100;

      const quickUpdateTimerId = setTimeout(() => {
        requestAnimationFrame(calculateDockedPosition);
      }, initialQuickDelay);

      window.addEventListener("resize", calculateDockedPosition);

      return () => {
        clearTimeout(quickUpdateTimerId);
        window.removeEventListener("resize", calculateDockedPosition);
      };
    } else {
      setDockedStyles(DOCKED_STYLES_BASE);
    }
  }, [
    embedPosition,
    location.pathname,
    location.search,
    calculateDockedPosition,
    isSidebarOpen,
  ]);

  // Effect for managing floating/docked state based on route
  useEffect(() => {
    const currentPath = location.pathname;
    const isOnDashboard = currentPath.includes("/dashboard");

    if (isOnDashboard) {
      dispatch(setEmbedPosition("docked"));
    }
    previousPathRef.current = currentPath;
  }, [location.pathname, dispatch, embedPosition]);

  const getEmbedStyles = () => {
    if (embedPosition === "docked") {
      return dockedStyles;
    }
    return DOCKED_STYLES_BASE;
  };

  const handleEmbedded = (embeddedReport: Embed) => {
    window.report = embeddedReport as unknown as Report;
  };

  if (isLoading) {
    return (
      <BaseLayout
        ref={layoutContainerRef}
        component={<LoadingView />}
        dockedStyles={dockedStyles}
        isSidebarOpen={isSidebarOpen}
      >
        {children}
      </BaseLayout>
    );
  }

  if (error) {
    return (
      <BaseLayout
        ref={layoutContainerRef}
        component={<ErrorView message={error.message} />}
        dockedStyles={dockedStyles}
        isSidebarOpen={isSidebarOpen}
      >
        {children}
      </BaseLayout>
    );
  }

  if (!hasDashboard) {
    return (
      <BaseLayout
        ref={layoutContainerRef}
        dockedStyles={dockedStyles}
        component={<NoDashboard />}
        isSidebarOpen={isSidebarOpen}
      >
        {children}
      </BaseLayout>
    );
  }

  if (!embedConfig) {
    return (
      <BaseLayout
        ref={layoutContainerRef}
        component={<LoadingView />}
        dockedStyles={dockedStyles}
        isSidebarOpen={isSidebarOpen}
      >
        {children}
      </BaseLayout>
    );
  }

  return (
    <Box
      className="analysis-layout-container"
      ref={layoutContainerRef}
      pos="relative"
      h="100%"
    >
      {embedConfig && (
        <Box
          sx={{
            ...getEmbedStyles(),
            maxWidth: isSidebarOpen
              ? "calc(100dvw - 19.5rem)"
              : "calc(100dvw - 4rem)",
            transition: isSidebarOpen ? "none" : "max-width 250ms ease",
          }}
          data-sidebar-state={isSidebarOpen ? "expanded" : "collapsed"}
          id="persistent-powerbi-embed-wrapper"
        >
          {isOutdated && <OutdatedAlert />}
          <PowerBIEmbed embedConfig={embedConfig} onEmbedded={handleEmbedded} />
        </Box>
      )}

      <Box className="outlet-container" h="100%">
        {children}
      </Box>
    </Box>
  );
};

export const AnalysisLayoutComponent = memo(AnalysisLayout);
